import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../../../hooks/useImagesLoaded';
import { COLOR_BLACK_STANDARD, COLOR_STEAK_SAUCE, COLOR_WHITE } from '../../../colors';
import { FUTURABOOK } from '../../../fonts';
import { addCommas } from '../../../../utils';

const COPYRIGHT_YEAR = new Date().getFullYear();
const useStyles = makeStyles((theme)=> ({
    wrap: {
        flex: 1,
        boxSizing: 'border-box',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: COLOR_STEAK_SAUCE,
        color: COLOR_WHITE,
        position: 'relative',
        fontFamily: ['futurabook', 'Arial', 'Helvetica', 'sans-serif'],
        fontWeight: 500,
    },
    wrapInfo: {
        padding: '36px 36px 0px 36px',
    },
    shadowGradient: {
        position: 'absolute',
        width: '100%',
        height: 370,
        bottom: 0,
        left: 0,
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.75) 100%);',
    },
    contentWrap: {
        boxSizing: 'border-box',
        textAlign: 'left',
        fontSize: '28px',
        lineHeight: '32px',
        zIndex: 2,
        position: 'relative',
        top: 0,
    },
    complianceWrap: {
        boxSizing: 'border-box',
        width: '100%',
        fontSize: '10px',
        lineHeight: '14px',
        letterSpacing: '0.4px',
        marginTop: '18px',
        zIndex: 444,
        backgroundColor: COLOR_BLACK_STANDARD,
        padding: '14px 24px 14px 24px',
    },
    disclaimerWrap: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    lrLogo: {
        display: 'block',
        height: '12px',
    },
    logoWrapper: {
        display: 'flex',
        alignItems: 'flex-end',
    },
    realtorLogo: {
        marginLeft: '20px',
        height: '24px',
    },
    equalHousingLogo: {
        marginLeft: '20px',
        height: '24px',
    },
}));

export default function Fullscreen({
    templateContent = {},
    currentFrame = 1,
    onImagesLoaded = null,
}) {
    const classes = useStyles();
    const {
        hero,
        address,
        city,
        state,
        photos,
        bedrooms,
        bathrooms,
        home_size,
        price_amount,
        open_houses = [],
        listing_status_label,
        realtor_logo_required = false,
    } = templateContent || {};
    const heroImage = (photos && photos.length) ? photos[0] : hero;
    const sqft = parseInt((home_size || '').replace(/\D/g, ''), 10) > 0 ? parseInt(home_size.replace(/\D/g, ''), 10) : null;
    const openHouses = open_houses || [];
    const loadedBgs = useImagesLoaded([heroImage]);
    if(!heroImage) {
        window.isLoaded = true;
    }

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
        return ()=> {
            window.isLoaded = false;
        };
    }, [loadedBgs, onImagesLoaded]);

    const display_address = address?.length > 0;

    return (
        <>
            <link rel="stylesheet" href={FUTURABOOK} />


                {/* Left Block */}

                <div className={classes.wrap}
                    style={{
                        backgroundImage: `url("${heroImage}")`
                    }}
                >
                    <div className={classes.wrapInfo}>

                        <div className={classes.shadowGradient}></div>
                        <div className={classes.contentWrap}>

                            <div style={{
                                fontSize: '36px',
                                lineHeight: '48px',
                                fontWeight: 600,
                                marginTop: openHouses.length ? 0 : 42,
                            }}>
                                {`${listing_status_label || 'Just Listed'}`}
                            </div>

                            {openHouses.length > 0 && (
                                openHouses.map((openHouse)=> {
                                    return (
                                        <div key={`${openHouse.date}-${openHouse.time}`}>
                                            {openHouse.date} &bull; {openHouse.time}
                                        </div>
                                    );
                                })
                            )}

                            {openHouses.length > 0 && (
                                <div style={{
                                    marginTop: 24,
                                    marginBottom: 24,
                                    width: 42,
                                    height: 0,
                                    borderTop: `4px solid ${COLOR_WHITE}`,
                                }}></div>
                            )}

                            {price_amount && openHouses.length === 0 && (<div style={{
                                fontSize: '36px',
                                lineHeight: '48px',
                                marginTop: 4
                            }}>
                                {`$${addCommas(price_amount)}`}
                            </div>)}


                            <div style={{marginTop: 4}}>
                                {price_amount && openHouses.length > 0 && (<span>{`$${addCommas(price_amount)}`}</span>)}{' '}
                                {price_amount && openHouses.length > 0 && (bedrooms > 0 || bathrooms > 0 || sqft) && (<span>&bull;</span>)}{' '}

                                {bedrooms > 0 && (<span>{bedrooms} bd</span>)}{' '}
                                {bedrooms > 0 && (bathrooms > 0 || sqft) && (<span>&bull;</span>)}{' '}

                                {bathrooms > 0 && (<span>{bathrooms} ba</span>)}{' '}
                                {bathrooms > 0 && (sqft && (<span>&bull;</span>))}{' '}

                                {sqft && (<span>{addCommas(sqft)} sqft</span>)}{' '}
                            </div>

                            {!display_address && city && (
                                <div>in {city}, {state}</div>
                            )}

                            {display_address && (
                                address.map((addressLine)=> <div key={addressLine}>{addressLine}</div>)
                            )}      
                        </div>
                    </div>

                    <div className={classes.complianceWrap}>
                        <div className={classes.disclaimerWrap}>
                            <div>
                                <img src="./images/lr-logo-disclaimer-white.svg" className={classes.lrLogo} alt="LR Logo" />
                                <div>&copy; ListReports {COPYRIGHT_YEAR}, all rights reserved</div>
                            </div>
                            <div>
                                This information is from third-party sources and its accuracy cannot be guaranteed
                            </div>
                            <div className={classes.logoWrapper}>
                                {realtor_logo_required && 
                                    <img src="./images/realtor-logo-white.svg" className={classes.realtorLogo} alt="Realtor logo" />
                                }
                                <img src="./images/equal-housing-logo-white.svg" className={classes.equalHousingLogo} alt="Equal Housing logo" />
                            </div>
                        </div>
                    </div>
                </div>


                {/* Right Block */}

        </>
    );
}
